/**
 * ThumbsFeedback — Universelles Daumen-hoch/runter Feedback
 *
 * Kann überall in der App eingebettet werden:
 * - Inline nach einer Session (Coach, Audio, Voice)
 * - Am Ende einer Seite (Tagesroutine, Rezepte)
 * - Als Floating-Button auf jeder Seite
 *
 * Props:
 * - clientId: Client-Identifikation
 * - sessionType: Art der Interaktion
 * - sessionRef: Optionale Referenz (Session-ID etc.)
 * - context: Seiten-Pfad oder Feature-Name
 * - variant: "inline" | "floating" | "card" | "minimal"
 * - label: Optionaler Text über den Daumen
 * - onFeedbackGiven: Callback nach Abgabe
 */
import { useState, useCallback } from "react";
import { ThumbsUp, ThumbsDown, Heart, X, Send } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

type SessionType = "chat" | "audio" | "voice" | "routine" | "rezept" | "session" | "allgemein";

interface ThumbsFeedbackProps {
  clientId: string;
  sessionType: SessionType;
  sessionRef?: string;
  context?: string;
  variant?: "inline" | "floating" | "card" | "minimal";
  label?: string;
  onFeedbackGiven?: (rating: number) => void;
  /** Dark mode for overlays (voice chat, session end) */
  dark?: boolean;
}

export function ThumbsFeedback({
  clientId,
  sessionType,
  sessionRef,
  context,
  variant = "inline",
  label,
  onFeedbackGiven,
  dark = false,
}: ThumbsFeedbackProps) {
  const [submitted, setSubmitted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [selectedRating, setSelectedRating] = useState<number | null>(null);

  const feedbackMutation = trpc.feedback.submit.useMutation();
  const canGiveQuery = trpc.feedback.canGive.useQuery(
    { clientId, sessionType, sessionRef, context },
    { enabled: !!clientId }
  );

  const handleRate = useCallback(
    (rating: number) => {
      setSelectedRating(rating);
      // For negative feedback, show comment box
      if (rating === -1) {
        setShowComment(true);
        return;
      }
      // Positive feedback: submit immediately
      feedbackMutation.mutate(
        { clientId, sessionType, sessionRef, rating, context },
        {
          onSuccess: (res) => {
            if (res.success) {
              setSubmitted(true);
              onFeedbackGiven?.(rating);
            }
          },
          onError: () => toast.error("Feedback konnte nicht gesendet werden"),
        }
      );
    },
    [clientId, sessionType, sessionRef, context, feedbackMutation, onFeedbackGiven]
  );

  const handleSubmitWithComment = useCallback(() => {
    if (selectedRating === null) return;
    feedbackMutation.mutate(
      {
        clientId,
        sessionType,
        sessionRef,
        rating: selectedRating,
        comment: comment.trim() || undefined,
        context,
      },
      {
        onSuccess: (res) => {
          if (res.success) {
            setSubmitted(true);
            onFeedbackGiven?.(selectedRating);
          }
        },
        onError: () => toast.error("Feedback konnte nicht gesendet werden"),
      }
    );
  }, [clientId, sessionType, sessionRef, selectedRating, comment, context, feedbackMutation, onFeedbackGiven]);

  // Don't show if already given feedback for this context
  if (canGiveQuery.data && !canGiveQuery.data.canGive) return null;
  if (dismissed) return null;

  // ─── Thank you state ───
  if (submitted) {
    if (variant === "minimal") {
      return (
        <span className={`text-xs ${dark ? "text-white/60" : "text-[#6b7f4e]"} animate-in fade-in duration-300`}>
          Danke!
        </span>
      );
    }
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl animate-in fade-in duration-300 ${
        dark ? "bg-white/10" : "bg-[#f0f3eb] border border-[#d4dcc4]"
      }`}>
        <Heart className={`w-4 h-4 ${dark ? "text-[#8ba366]" : "text-[#6b7f4e]"}`} />
        <span className={`text-xs ${dark ? "text-white/70" : "text-[#4a5e38]"}`}>
          Danke für dein Feedback!
        </span>
      </div>
    );
  }

  // ─── Comment box (after thumbs down) ───
  if (showComment) {
    return (
      <div className={`rounded-xl p-3 animate-in fade-in slide-in-from-bottom-2 duration-300 ${
        dark ? "bg-white/10 backdrop-blur-sm" : "bg-[#f0f3eb] border border-[#d4dcc4]"
      }`}>
        <div className="flex items-center justify-between mb-2">
          <p className={`text-xs font-medium ${dark ? "text-white/80" : "text-[#2d3a1e]"}`}>
            Was können wir verbessern?
          </p>
          <button
            onClick={() => { setShowComment(false); setSelectedRating(null); }}
            className={`p-0.5 transition-colors ${dark ? "text-white/40 hover:text-white/70" : "text-[#8a9680] hover:text-[#5a6b4a]"}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional: Dein Verbesserungsvorschlag..."
          className={`w-full rounded-lg px-3 py-2 text-xs resize-none h-14 focus:outline-none transition-colors ${
            dark
              ? "bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-white/20"
              : "bg-white border border-[#d4dcc4] text-[#2d3a1e] placeholder:text-[#8a9680] focus:border-[#6b7f4e]"
          }`}
        />
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSubmitWithComment}
            disabled={feedbackMutation.isPending}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              dark
                ? "bg-[#6b7f4e] hover:bg-[#5a6d3e] text-white"
                : "bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white"
            }`}
          >
            <Send className="w-3 h-3" />
            Absenden
          </button>
          <button
            onClick={() => {
              setComment("");
              handleSubmitWithComment();
            }}
            className={`text-xs transition-colors ${dark ? "text-white/40 hover:text-white/60" : "text-[#8a9680] hover:text-[#5a6b4a]"}`}
          >
            Ohne Kommentar
          </button>
        </div>
      </div>
    );
  }

  // ─── Minimal variant (just two small icons) ───
  if (variant === "minimal") {
    return (
      <div className="flex items-center gap-1.5 animate-in fade-in duration-300">
        <button
          onClick={() => handleRate(1)}
          disabled={feedbackMutation.isPending}
          className={`p-1 rounded-md transition-all hover:scale-110 ${
            dark ? "hover:bg-white/10 text-white/50 hover:text-white/80" : "hover:bg-[#6b7f4e]/10 text-[#8a9680] hover:text-[#6b7f4e]"
          }`}
          title="Gut"
        >
          <ThumbsUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => handleRate(-1)}
          disabled={feedbackMutation.isPending}
          className={`p-1 rounded-md transition-all hover:scale-110 ${
            dark ? "hover:bg-white/10 text-white/50 hover:text-white/80" : "hover:bg-red-50 text-[#8a9680] hover:text-red-500"
          }`}
          title="Nicht so gut"
        >
          <ThumbsDown className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // ─── Card variant (for end-of-session screens) ───
  if (variant === "card") {
    return (
      <div className={`rounded-xl p-4 animate-in fade-in slide-in-from-bottom-2 duration-300 ${
        dark ? "bg-white/5 backdrop-blur-sm border border-white/10" : "bg-[#f0f3eb] border border-[#d4dcc4]"
      }`}>
        <p className={`text-sm font-medium text-center mb-3 ${dark ? "text-white/80" : "text-[#2d3a1e]"}`}>
          {label || "Wie war das?"}
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => handleRate(1)}
            disabled={feedbackMutation.isPending}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border transition-all ${
              dark
                ? "border-white/10 hover:bg-[#6b7f4e]/20 hover:border-[#6b7f4e]/30 text-white"
                : "border-[#d4dcc4] hover:bg-[#6b7f4e]/10 hover:border-[#6b7f4e]/30 text-[#2d3a1e]"
            }`}
          >
            <ThumbsUp className={`w-4 h-4 ${dark ? "text-[#8ba366]" : "text-[#6b7f4e]"}`} />
            <span className="text-sm">Gut</span>
          </button>
          <button
            onClick={() => handleRate(-1)}
            disabled={feedbackMutation.isPending}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border transition-all ${
              dark
                ? "border-white/10 hover:bg-red-500/10 hover:border-red-500/20 text-white"
                : "border-[#d4dcc4] hover:bg-red-50 hover:border-red-200 text-[#2d3a1e]"
            }`}
          >
            <ThumbsDown className={`w-4 h-4 ${dark ? "text-white/50" : "text-[#8a9680]"}`} />
            <span className="text-sm">Nicht so</span>
          </button>
        </div>
      </div>
    );
  }

  // ─── Inline variant (default — compact row) ───
  return (
    <div className={`flex items-center gap-3 animate-in fade-in duration-300 ${
      variant === "floating" ? "fixed bottom-24 lg:bottom-6 right-4 z-40" : ""
    }`}>
      {variant === "floating" ? (
        <div className="bg-white shadow-lg border border-[#d4dcc4] rounded-2xl px-4 py-2.5 flex items-center gap-3">
          <span className="text-xs text-[#5a6b4a] font-medium whitespace-nowrap">
            {label || "Wie findest du diese Seite?"}
          </span>
          <div className="flex gap-1.5">
            <button
              onClick={() => handleRate(1)}
              disabled={feedbackMutation.isPending}
              className="p-1.5 rounded-lg hover:bg-[#6b7f4e]/10 text-[#8a9680] hover:text-[#6b7f4e] transition-all hover:scale-110"
              title="Gut"
            >
              <ThumbsUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleRate(-1)}
              disabled={feedbackMutation.isPending}
              className="p-1.5 rounded-lg hover:bg-red-50 text-[#8a9680] hover:text-red-500 transition-all hover:scale-110"
              title="Nicht so gut"
            >
              <ThumbsDown className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 text-[#8a9680] hover:text-[#5a6b4a] transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <>
          {label && (
            <span className="text-xs text-[#8a9680]">{label}</span>
          )}
          <div className="flex gap-1.5">
            <button
              onClick={() => handleRate(1)}
              disabled={feedbackMutation.isPending}
              className="p-1.5 rounded-lg hover:bg-[#6b7f4e]/10 text-[#8a9680] hover:text-[#6b7f4e] transition-all hover:scale-110"
              title="Gut"
            >
              <ThumbsUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleRate(-1)}
              disabled={feedbackMutation.isPending}
              className="p-1.5 rounded-lg hover:bg-red-50 text-[#8a9680] hover:text-red-500 transition-all hover:scale-110"
              title="Nicht so gut"
            >
              <ThumbsDown className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}


